import React, { useState } from 'react';
import { Card, Tabs, Table, Tag, Button, App, Space, Select, Typography, Descriptions, Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, EyeOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  useGetPendingShiftClosingsQuery,
  useGetShiftClosingHistoryQuery,
  useReviewShiftClosingMutation,
  useGetShiftClosingQuery,
} from '../services/shiftClosingApi';
import { ShiftClosingListDto, ReviewShiftClosingDto } from '../types/cashRegister';
import ReviewShiftClosingModal from '../components/cashRegister/ReviewShiftClosingModal';
import ViewShiftClosingModal from '../components/cashRegister/ViewShiftClosingModal';
import { useCurrency } from '../hooks/useCurrency';
import { useResponsive } from '../hooks/useResponsive';
import { useRestaurantTimeZone } from '../hooks/useTimeZone';
import dayjs from 'dayjs';
import MobileDateRangePicker from '../components/common/MobileDateRangePickerV2';

const { Text } = Typography;
const { Option } = Select;

const ShiftClosings: React.FC = () => {
  const { message } = App.useApp();
  const { format: formatCurrency } = useCurrency();
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const [selectedShiftClosingId, setSelectedShiftClosingId] = useState<string | null>(null);
  const [reviewModalVisible, setReviewModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');

  // History filters
  const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null]>([null, null]);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);

  // API queries
  const { data: pendingClosings, isLoading: loadingPending, refetch: refetchPending } = useGetPendingShiftClosingsQuery();

  const { data: history, isLoading: loadingHistory, refetch: refetchHistory } = useGetShiftClosingHistoryQuery({
    fromDate: dateRange[0]?.toISOString(),
    toDate: dateRange[1]?.toISOString(),
    status: statusFilter,
  });

  const { data: selectedShiftClosing } = useGetShiftClosingQuery(selectedShiftClosingId!, {
    skip: !selectedShiftClosingId,
  });

  const [reviewShiftClosing, { isLoading: reviewing }] = useReviewShiftClosingMutation();

  const getStatusTag = (status: string) => {
    const statusConfig: Record<string, { color: string; text: string; icon: React.ReactNode }> = {
      Pending: {
        color: 'orange',
        text: 'Pendiente',
        icon: <ClockCircleOutlined />
      },
      Approved: {
        color: 'success',
        text: 'Aprobado',
        icon: <CheckCircleOutlined />
      },
      Rejected: {
        color: 'error',
        text: 'Rechazado',
        icon: <CloseCircleOutlined />
      },
    };

    const config = statusConfig[status] || { color: 'default', text: status, icon: null };
    return (
      <Tag color={config.color} icon={config.icon}>
        {config.text}
      </Tag>
    );
  };

  const getDifferenceDisplay = (difference: number) => {
    if (difference === 0) {
      return <span style={{ color: 'var(--color-success-text)', fontWeight: 'bold' }}>✓ Exacto</span>;
    }
    const color = difference > 0 ? '#cf1322' : '#fa8c16';
    const prefix = difference > 0 ? '+' : '';
    return (
      <span style={{ color, fontWeight: 'bold' }}>
        {prefix}{formatCurrency(difference)}
      </span>
    );
  };

  const renderMobileCard = (record: ShiftClosingListDto, showReviewInfo: boolean = false) => (
    <Card
      key={record.id}
      style={{ marginBottom: 12 }}
      size="small"
    >
      <Space direction="vertical" style={{ width: '100%' }} size="small">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Text strong>{record.createdByUserName}</Text>
          {getStatusTag(record.status)}
        </div>

        <Text type="secondary" style={{ fontSize: '12px' }}>
          {formatInTimeZone(record.createdAt, 'DD/MM/YYYY HH:mm')}
        </Text>

        <Descriptions size="small" column={1} bordered>
          <Descriptions.Item label="Total Contado">
            <Text strong style={{ color: '#1890ff' }}>{formatCurrency(record.countedTotal)}</Text>
          </Descriptions.Item>
          <Descriptions.Item label="Total Sistema">
            {formatCurrency(record.systemTotal)}
          </Descriptions.Item>
          <Descriptions.Item label="Diferencia">
            {getDifferenceDisplay(record.totalDifference)}
          </Descriptions.Item>
        </Descriptions>

        {showReviewInfo && record.reviewedByUserName && (
          <>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              Revisado por: {record.reviewedByUserName}
            </Text>
            {record.reviewedAt && (
              <Text type="secondary" style={{ fontSize: '12px' }}>
                {formatInTimeZone(record.reviewedAt, 'DD/MM/YYYY HH:mm')}
              </Text>
            )}
          </>
        )}

        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleReview(record.id)}
          block
          size="small"
        >
          {showReviewInfo ? 'Ver Detalles' : 'Revisar'}
        </Button>
      </Space>
    </Card>
  );

  const handleReview = (shiftClosingId: string) => {
    setSelectedShiftClosingId(shiftClosingId);
    setReviewModalVisible(true);
  };

  const handleViewDetails = (shiftClosingId: string) => {
    setSelectedShiftClosingId(shiftClosingId);
    setViewModalVisible(true);
  };

  const handleRefresh = async () => {
    if (activeTab === 'pending') {
      await refetchPending();
      message.success('Cierres pendientes actualizados');
    } else {
      await refetchHistory();
      message.success('Historial actualizado');
    }
  };

  const handleReviewSubmit = async (data: ReviewShiftClosingDto) => {
    if (!selectedShiftClosingId) return;

    try {
      await reviewShiftClosing({
        id: selectedShiftClosingId,
        data,
      }).unwrap();

      const actionText = data.action === 'Approve' ? 'aprobado' : 'rechazado';
      message.success(`Cierre de turno ${actionText} exitosamente`);
      setReviewModalVisible(false);
      setSelectedShiftClosingId(null);
    } catch (error: any) {
      message.error(error.data?.message || 'Error al revisar cierre de turno');
    }
  };

  const pendingColumns = [
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatInTimeZone(date, 'DD/MM/YYYY HH:mm'),
      sorter: (a: ShiftClosingListDto, b: ShiftClosingListDto) =>
        dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      responsive: ['md'] as any,
    },
    {
      title: 'Empleado',
      dataIndex: 'createdByUserName',
      key: 'createdByUserName',
    },
    {
      title: 'Caja Registradora',
      dataIndex: 'cashRegisterUserName',
      key: 'cashRegisterUserName',
      responsive: ['lg'] as any,
    },
    {
      title: 'Total Contado',
      dataIndex: 'countedTotal',
      key: 'countedTotal',
      render: (amount: number) => formatCurrency(amount),
      align: 'right' as const,
      responsive: ['md'] as any,
    },
    {
      title: 'Total Sistema',
      dataIndex: 'systemTotal',
      key: 'systemTotal',
      render: (amount: number) => formatCurrency(amount),
      align: 'right' as const,
      responsive: ['md'] as any,
    },
    {
      title: 'Diferencia',
      dataIndex: 'totalDifference',
      key: 'totalDifference',
      render: getDifferenceDisplay,
      align: 'center' as const,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag,
      align: 'center' as const,
      responsive: ['md'] as any,
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: ShiftClosingListDto) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleReview(record.id)}
          size={isMobile ? 'small' : 'middle'}
        >
          {isMobile ? 'Ver' : 'Revisar'}
        </Button>
      ),
      align: 'center' as const,
    },
  ];

  const historyColumns = [
    {
      title: 'Fecha',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatInTimeZone(date, 'DD/MM/YYYY HH:mm'),
      sorter: (a: ShiftClosingListDto, b: ShiftClosingListDto) =>
        dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      responsive: ['md'] as any,
    },
    {
      title: 'Empleado',
      dataIndex: 'createdByUserName',
      key: 'createdByUserName',
    },
    {
      title: 'Total Contado',
      dataIndex: 'countedTotal',
      key: 'countedTotal',
      render: (amount: number) => formatCurrency(amount),
      align: 'right' as const,
      responsive: ['lg'] as any,
    },
    {
      title: 'Total Sistema',
      dataIndex: 'systemTotal',
      key: 'systemTotal',
      render: (amount: number) => formatCurrency(amount),
      align: 'right' as const,
      responsive: ['lg'] as any,
    },
    {
      title: 'Diferencia',
      dataIndex: 'totalDifference',
      key: 'totalDifference',
      render: getDifferenceDisplay,
      align: 'center' as const,
      responsive: ['md'] as any,
    },
    {
      title: 'Estado',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag,
      align: 'center' as const,
    },
    {
      title: 'Revisado Por',
      dataIndex: 'reviewedByUserName',
      key: 'reviewedByUserName',
      render: (name: string | undefined) => name || '-',
      responsive: ['xl'] as any,
    },
    {
      title: 'Fecha Revisión',
      dataIndex: 'reviewedAt',
      key: 'reviewedAt',
      render: (date: string | undefined) => date ? formatInTimeZone(date, 'DD/MM/YYYY HH:mm') : '-',
      responsive: ['xl'] as any,
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: ShiftClosingListDto) => (
        <Button
          type="primary"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(record.id)}
          size={isMobile ? 'small' : 'middle'}
        >
          {isMobile ? 'Ver' : 'Ver Detalles'}
        </Button>
      ),
      align: 'center' as const,
    },
  ];

  return (
    <div style={{ padding: isMobile ? 8 : 24 }}>
      <Card
        title="Cierres de Turno"
        extra={
          <Tooltip title="Actualizar">
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
              loading={activeTab === 'pending' ? loadingPending : loadingHistory}
              size={isMobile ? 'small' : 'middle'}
            >
              {!isMobile && 'Actualizar'}
            </Button>
          </Tooltip>
        }
      >
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <Tabs.TabPane
            tab={
              <span>
                <ClockCircleOutlined />
                {isMobile ? 'Pend.' : 'Pendientes'} {pendingClosings && pendingClosings.length > 0 ? `(${pendingClosings.length})` : ''}
              </span>
            }
            key="pending"
          >
            {isMobile ? (
              loadingPending ? (
                <div style={{ textAlign: 'center', padding: 20 }}>Cargando...</div>
              ) : pendingClosings && pendingClosings.length > 0 ? (
                pendingClosings.map(record => renderMobileCard(record, false))
              ) : (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--color-neutral-text)' }}>
                  No hay cierres de turno pendientes
                </div>
              )
            ) : (
              <Table
                columns={pendingColumns}
                dataSource={pendingClosings}
                rowKey="id"
                loading={loadingPending}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
              />
            )}
          </Tabs.TabPane>

          <Tabs.TabPane
            tab={
              <span>
                <CheckCircleOutlined />
                {isMobile ? 'Hist.' : 'Historial'}
              </span>
            }
            key="history"
          >
            <Space
              style={{ marginBottom: 16, width: '100%' }}
              wrap
              direction={isMobile ? 'vertical' : 'horizontal'}
              size={isMobile ? 'small' : 'middle'}
            >
              <MobileDateRangePicker
                value={dateRange as any}
                onChange={(dates) => setDateRange(dates as any)}
                placeholder={['Desde', 'Hasta']}
                size={isMobile ? 'small' : 'middle'}
                style={{ width: isMobile ? '100%' : 'auto' }}
              />
              <Select
                style={{ width: isMobile ? '100%' : 200 }}
                placeholder="Filtrar por estado"
                value={statusFilter}
                onChange={setStatusFilter}
                allowClear
                size={isMobile ? 'small' : 'middle'}
              >
                <Option value="Pending">Pendiente</Option>
                <Option value="Approved">Aprobado</Option>
                <Option value="Rejected">Rechazado</Option>
              </Select>
            </Space>

            {isMobile ? (
              loadingHistory ? (
                <div style={{ textAlign: 'center', padding: 20 }}>Cargando...</div>
              ) : history && history.length > 0 ? (
                history.map(record => renderMobileCard(record, true))
              ) : (
                <div style={{ textAlign: 'center', padding: 20, color: 'var(--color-neutral-text)' }}>
                  No hay registros en el historial
                </div>
              )
            ) : (
              <Table
                columns={historyColumns}
                dataSource={history}
                rowKey="id"
                loading={loadingHistory}
                pagination={{ pageSize: 10 }}
                scroll={{ x: 'max-content' }}
              />
            )}
          </Tabs.TabPane>
        </Tabs>
      </Card>

      <ReviewShiftClosingModal
        visible={reviewModalVisible}
        shiftClosing={selectedShiftClosing || null}
        onCancel={() => {
          setReviewModalVisible(false);
          setSelectedShiftClosingId(null);
        }}
        onSubmit={handleReviewSubmit}
        loading={reviewing}
      />

      <ViewShiftClosingModal
        visible={viewModalVisible}
        shiftClosing={selectedShiftClosing || null}
        onClose={() => {
          setViewModalVisible(false);
          setSelectedShiftClosingId(null);
        }}
      />
    </div>
  );
};

export default ShiftClosings;
